// src/App.jsx
import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AnimatePresence, motion } from 'framer-motion';
import Header from './components/Header';
import Footer from './components/Footer';
import PageLoader from './components/PageLoader';
import BackToTop from './components/BackToTop';
import WhatsAppButton from './components/WhatsAppButton';
import FloatingTrustWidget from './components/FloatingTrustWidget';
import './styles/main.css';
import './styles/global.css';
import './styles/header.css';
import './styles/reviews.css'; // Import reviews CSS

// Lazy-loaded pages for code splitting & speed optimization
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const TourPackages = lazy(() => import('./pages/TourPackages'));
const Contact = lazy(() => import('./pages/Contact'));
const Gallery = lazy(() => import('./pages/Gallery'));
const CarInfo = lazy(() => import('./pages/CarInfo'));
const Reviews = lazy(() => import('./pages/Reviews'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const FAQ = lazy(() => import('./pages/FAQ'));

// Page transition animation variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
    scale: 0.98
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut",
      when: "beforeChildren",
      staggerChildren: 0.1
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.98,
    transition: {
      duration: 0.3,
      ease: "easeIn"
    }
  }
};

// Content animation for staggered loading
const contentVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

// Custom AnimatedRoutes component
const AnimatedRoutes = () => {
  const location = useLocation();
  const [isFirstMount, setIsFirstMount] = useState(true);

  useEffect(() => {
    if (isFirstMount) {
      setIsFirstMount(false);
    }
  }, [isFirstMount]);

  return (
    <Suspense fallback={<PageLoader />}>
      <AnimatePresence mode="wait" initial={false}>
        <Routes location={location} key={location.pathname}>
          <Route 
            path="/" 
            element={
              <motion.div
                key="home"
                variants={pageVariants}
                initial={isFirstMount ? false : "initial"}
                animate="animate"
                exit="exit"
                className="page-wrapper"
              >
                <motion.div variants={contentVariants}>
                  <Home />
                </motion.div>
              </motion.div>
            } 
          />
          <Route 
            path="/about" 
            element={
              <motion.div
                key="about"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="page-wrapper"
              >
                <motion.div variants={contentVariants}>
                  <About />
                </motion.div>
              </motion.div>
            } 
          />
          <Route 
            path="/services" 
            element={
              <motion.div
                key="services"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="page-wrapper"
              >
                <motion.div variants={contentVariants}>
                  <Services />
                </motion.div>
              </motion.div>
            } 
          />
          <Route 
            path="/tours" 
            element={
              <motion.div
                key="tours"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="page-wrapper"
              >
                <motion.div variants={contentVariants}>
                  <TourPackages />
                </motion.div>
              </motion.div>
            } 
          />
          <Route 
            path="/contact" 
            element={
              <motion.div
                key="contact"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="page-wrapper"
              >
                <motion.div variants={contentVariants}>
                  <Contact />
                </motion.div>
              </motion.div>
            } 
          />
          <Route 
            path="/gallery" 
            element={
              <motion.div
                key="gallery"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="page-wrapper"
              >
                <motion.div variants={contentVariants}>
                  <Gallery />
                </motion.div>
              </motion.div>
            } 
          />
          <Route 
            path="/cars" 
            element={
              <motion.div
                key="cars"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="page-wrapper"
              >
                <motion.div variants={contentVariants}>
                  <CarInfo />
                </motion.div>
              </motion.div>
            } 
          />
          <Route 
            path="/reviews" 
            element={
              <motion.div
                key="reviews"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="page-wrapper"
              >
                <motion.div variants={contentVariants}>
                  <Reviews />
                </motion.div>
              </motion.div>
            } 
          />
          <Route 
            path="/terms" 
            element={
              <motion.div
                key="terms"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="page-wrapper"
              >
                <motion.div variants={contentVariants}>
                  <Terms />
                </motion.div>
              </motion.div>
            } 
          />
          <Route 
            path="/privacy" 
            element={
              <motion.div
                key="privacy"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="page-wrapper"
              >
                <motion.div variants={contentVariants}>
                  <Privacy />
                </motion.div>
              </motion.div>
            } 
          />
          <Route 
            path="/faq" 
            element={
              <motion.div
                key="faq"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="page-wrapper"
              >
                <motion.div variants={contentVariants}>
                  <FAQ />
                </motion.div>
              </motion.div>
            } 
          />
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [pathname]);

  return null;
};

// Progress bar component for page transitions
const PageTransitionProgress = ({ isAnimating }) => {
  return (
    <motion.div
      className="page-transition-progress"
      initial={{ scaleX: 0 }}
      animate={{ 
        scaleX: isAnimating ? 1 : 0,
        transition: {
          duration: isAnimating ? 0.5 : 0,
          ease: "easeInOut"
        }
      }}
    />
  );
};

function App() {
  const [isTransitioning] = useState(false);

  return (
    <HelmetProvider>
      <Router>
        <div className="app">
          <PageTransitionProgress isAnimating={isTransitioning} />
          <Header />
          <ScrollToTop />
          <main className="main-content">
            <AnimatedRoutes />
          </main>
          <Footer />
          <WhatsAppButton />
          <FloatingTrustWidget />
          <BackToTop />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;